import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  CSSProperties,
  ComponentType,
  ReactNode,
} from 'react'
import './Button.css'

type Variant = 'primary' | 'secondary' | 'outline' | 'transparent'
type Size = 'tiny' | 'small' | 'medium' | 'large' | 'rectangle' | 'circle'

interface BaseProps {
  variant?: Variant
  size?: Size
  className?: string
  style?: CSSProperties
}

type ButtonAsButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
    as?: 'button'
  }
type ButtonAsLink = BaseProps & {
  as: 'link'
  linkEl: ComponentType<any>
  href?: string
  to?: string
  children?: ReactNode
}
type ButtonAsAnchor = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
    as: 'anchor'
    children?: ReactNode
  }

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor

function Button({
  className = '',
  variant = 'primary',
  size = 'medium',
  children,
  ...props
}: ButtonProps) {
  const attributes = {
    className: `Button ${className}`,
    'data-variant': variant,
    'data-size': size,
  }

  if (props.as === 'link') {
    const { as, linkEl: LinkEl, ...rest } = props
    return (
      <LinkEl {...attributes} {...rest}>
        {children}
      </LinkEl>
    )
  }

  if (props.as === 'anchor') {
    const { as, ...rest } = props
    return (
      <a {...attributes} {...rest}>
        {children}
      </a>
    )
  }

  const { as, ...rest } = props
  return (
    // eslint-disable-next-line react/button-has-type
    <button {...attributes} {...rest}>
      {children}
    </button>
  )
}

export default Button
